import { useCallback } from 'react'

import type { QueryObserverResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { ProcuradorUseCase } from '@/features/proposta/brasil/domain/Comprador/use-case/procurador-use-case'
import type { TProcuradorPropostaSchema } from '@/features/proposta/brasil/domain/schemas'
import type { CompradorVinculoStepSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { showToast } from '@/infra/sonner'
import type { TCliente } from '@/types/cliente'

type TUseDadosProcuradorProps = {
  refetch: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  onNext: () => void
  compradores: TCompradoresCadastrados
  procuradores: TProcuradorPropostaSchema[]
  nacionalidadePrincipalId: string
}

export function useDadosProcuradorVinculo({
  onNext,
  compradores,
  procuradores,
  refetch,
  nacionalidadePrincipalId,
}: TUseDadosProcuradorProps) {
  const { trigger, setValue, reset, watch } = useFormContext<CompradorVinculoStepSchemaInput>()
  const compradorAtual = watch()
  const cpfProcurador = compradorAtual.dados.cpf
  const buscouCpf = compradorAtual.dados.buscouCpf
  const cpfRaw = cpfProcurador?.replaceAll(/\D/g, '')

  const emailsJaVinculados = watch('emailsJaVinculados')

  const useCase = new ProcuradorUseCase(compradores, emailsJaVinculados, nacionalidadePrincipalId, procuradores)

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)

    if (!ok && error === 'MISSING_CPF') {
      trigger('dados.cpf')
      return
    }

    if (!ok && error === 'NEED_SEARCH') {
      showToast({
        variant: 'error',
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
      })
      return
    }

    if (await trigger('dados')) onNext()
  }, [cpfProcurador, buscouCpf, trigger, onNext])

  const handleBuscarCliente = useCallback(async () => {
    const result = await refetch()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)
    switch (fetchResult.type) {
      case 'SUCCESS': {
        const valicaoListaVinculos = useCase.validarSeCpfGeraListaVinculosVazia(fetchResult.patch.dados.id!)
        if (!valicaoListaVinculos.ok) {
          showToast({
            variant: 'error',
            message: 'Nenhum comprador elegível encontrado para vincular ao procurador.',
          })
          return
        }
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })
        reset(fetchResult.patch)
        break
      }
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.erro'),
        })
        break
    }
  }, [cpfRaw])

  const handleLimpar = useCallback(() => {
    reset(useCase.limparProcurador())
  }, [reset])

  return {
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
    },
  }
}
